import { SelectPaginateAsync } from "@/components/atoms/Select";
import useLoadOptions from "@/hooks/useLoadOptions";
import Course from "@/interfaces/Course";
import Instructor from "@/interfaces/Instructor";
import { useUpdateInstructorMutation } from "@/services/instructors";
import { faPen, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormEvent, useId, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { MultiValue } from "react-select";
import { toast } from "react-toastify";

const InstructorCourses = ({
  instructor,
  compact = false,
}: {
  instructor: Instructor;
  compact?: boolean;
}) => {
  const fieldId = useId();
  const titleId = useId();
  const [show, setShow] = useState(false);
  const [courses, setCourses] = useState<MultiValue<Course>>([]);
  const [error, setError] = useState<string>();
  const [updateInstructor, { isLoading }] = useUpdateInstructorMutation();
  const { loadCoursesOptions } = useLoadOptions();

  const open = () => {
    setCourses(instructor.courses ?? []);
    setError(undefined);
    setShow(true);
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (isLoading) return;
    setError(undefined);
    try {
      await updateInstructor([
        instructor.id,
        { courseIds: courses.map(({ id }) => id) },
      ]).unwrap();
      setShow(false);
      toast.success("Les formations du formateur ont été enregistrées.");
    } catch {
      setError("Impossible d’enregistrer les formations. Veuillez réessayer.");
    }
  };

  return (
    <section
      className={`instructor-courses${
        compact ? " instructor-courses--compact" : ""
      }`}
      aria-label="Formations que le formateur peut assurer"
    >
      <div className="instructor-courses__header">
        <div>
          <h3 className="instructor-courses__title">
            {compact ? "Formations" : "Formations du formateur"}
          </h3>
          {!compact && (
            <p className="instructor-courses__description">
              Les formations qu’il peut assurer.
            </p>
          )}
        </div>
        <Button
          size="sm"
          variant="light"
          className="instructor-courses__edit"
          aria-label={
            instructor.courses?.length
              ? "Modifier les formations"
              : "Ajouter une formation"
          }
          onClick={open}
        >
          <FontAwesomeIcon
            icon={instructor.courses?.length ? faPen : faPlus}
            aria-hidden="true"
          />
          {instructor.courses?.length ? "Modifier" : "Ajouter une formation"}
        </Button>
      </div>
      {instructor.courses?.length ? (
        <ul className="instructor-courses__list">
          {instructor.courses.map((course) => (
            <li key={course.id} className="instructor-courses__row">
              <span className="instructor-courses__name">{course.name}</span>
              {course.archived && (
                <span className="instructor-courses__status">Archivée</span>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <div className="instructor-courses__empty">
          <p>Aucune formation renseignée.</p>
          <p>
            Ajoutez une formation du catalogue pour préciser ses compétences.
          </p>
        </div>
      )}
      <Modal
        className="instructor-courses-modal"
        aria-labelledby={titleId}
        show={show}
        onHide={() => !isLoading && setShow(false)}
        centered
      >
        <Form onSubmit={save}>
          <Modal.Header closeButton={!isLoading}>
            <Modal.Title id={titleId}>Modifier les formations</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              Sélectionnez les formations que {instructor.fullname} peut
              assurer.
            </p>
            {error && <Alert variant="danger">{error}</Alert>}
            <Form.Label htmlFor={fieldId}>Formations du catalogue</Form.Label>
            <SelectPaginateAsync
              classNamePrefix="instructor-course-select"
              inputId={fieldId}
              isMulti
              isDisabled={isLoading}
              loadOptions={loadCoursesOptions}
              value={courses}
              onChange={setCourses}
              getOptionLabel={(course) => course.name}
              getOptionValue={(course) => course.id}
              placeholder="Rechercher une formation…"
            />
            <Form.Text>
              Retirez une formation de la sélection pour la dissocier du
              formateur.
            </Form.Text>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="light"
              disabled={isLoading}
              onClick={() => setShow(false)}
            >
              Annuler
            </Button>
            <Button variant="nolina-primary" type="submit" disabled={isLoading}>
              {isLoading ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </section>
  );
};

export default InstructorCourses;
